import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Place {
  place_id: string;
  name: string;
  images?: { file_url: string }[];
}

interface PlaceWithImages extends Place {
  loadedImages: string[];
}

const ExperiencesSection: React.FC = () => {
  const { data: places = [], isLoading: isPlacesLoading } = useQuery<Place[]>({
    queryKey: ["places", "attractions"],
    queryFn: async () => {
      const res = await fetch(
        `${BASE_URL}/api/places/?category=جاذبه‌های دیدنی&page=1&page_size=50`
      );
      if (!res.ok) throw new Error("خطا در دریافت جاذبه‌ها");
      const json = await res.json();
      return Array.isArray(json) ? json : json.data || [];
    },
    staleTime: 10 * 60 * 1000,
  });

  const [randomPlacesWithImages, setRandomPlacesWithImages] = useState<PlaceWithImages[]>([]);

  const fetchImageFileUrls = async (placeId: string): Promise<string[]> => {
    try {
      const res = await fetch(`${BASE_URL}/api/places/${placeId}/`);
      if (!res.ok) throw new Error("Failed to fetch images");
      const data = await res.json();
      return (data.data.images || []).map((img: any) => img.file_url);
    } catch {
      return [];
    }
  };

  const getImageUrl = (fileUrl?: string) => {
    if (!fileUrl) return "https://via.placeholder.com/150";
    if (fileUrl.startsWith("http")) return fileUrl;
    return `${BASE_URL}${fileUrl}`;
  };

  useEffect(() => {
    const loadRandomPlaces = async () => {
      if (places.length === 0) return;
      const shuffled = [...places].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, 6);
      const placesWithImagesPromises = selected.map(async (place) => {
        const urls = await fetchImageFileUrls(place.place_id);
        return {
          ...place,
          loadedImages: urls,
        };
      });
      const results = await Promise.all(placesWithImagesPromises);
      setRandomPlacesWithImages(results);
    };
    loadRandomPlaces();
  }, [places]);
  
  const openSurvey = () => {
    window.open("https://survey.porsline.ir/s/zq1g7a7x", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="relative desktop:mx-32 mb-4 overflow-hidden desktop:h-[400px] tablet:h-[320px] h-[620px]">
      {/* متن بالا - با pointer-events: none برای اجازه کلیک به آیتم‌های پشت */}
      <div className="relative z-[60] text-center mt-60 tablet:mt-0 desktop:mt-0">
        {/* دکمه با pointer-events: auto برای کلیک‌پذیر بودن */}
        <div className="pointer-events-auto inline-block">
          <h2
            className="text-lg tablet:text-[32px] desktop:text-[40px] font-myYekanDemibold text-gray-800 desktop:mb-8 mb-2 tablet:mb-6 tablet:mt-6"
            dir="rtl"
          >
            تجربه‌های خودتو به لوتکا اضافه کن
          </h2>
          <p
            className="text-base tablet:text-[20px] desktop:text-[24px] font-myYekanRegular text-gray-600 desktop:mb-2 mb-1"
            dir="rtl"
          >
            جایی که رفتی رو دوستش داشتی؟
          </p>
          <p
            className="text-base tablet:text-[20px] desktop:text-[24px] font-myYekanRegular text-gray-600 desktop:mb-8 mb-4"
            dir="rtl"
          >
            به بقیه هم پیشنهاد بده!
          </p>
          <div
            onClick={openSurvey}
            className="flex flex-row gap-1 items-center mx-auto justify-center border border-[#4D93A5] text-[#4D93A5] hover:bg-[#647B3B]/5 font-myYekanRegular desktop:py-4 py-2 rounded-lg desktop:w-[124px] tablet:w-[120px] w-[84px] h-[40px] desktop:text-[18px] tablet:text-base text-[14px] cursor-pointer"
          >
            <span className="pr-1">ثبت تجربه</span>
          </div>
        </div>
      </div>

      {/* عکس‌های شناور - Z-index بالاتر و pointer-events: auto */}
      <div className="absolute inset-0 font-myYekanRegular z-[70] pointer-events-none">
        {/* آیتم ۱ */}
        {randomPlacesWithImages[0] && (
          <Link
            to={`/places/${randomPlacesWithImages[0].place_id}`}
            className="place-link float3 absolute desktop:top-6 tablet:top-[10%] top-[20%] desktop:right-40 tablet:right-[12%] right-[10%] flex flex-col items-center w-24 hover:opacity-80 transition-opacity pointer-events-auto"
            style={{ zIndex: 70, cursor: 'pointer' }}
          >
            <div className="w-20 h-20 md:w-14 md:h-14 lg:w-24 lg:h-24 rounded-full overflow-hidden">
              <img
                src={getImageUrl(randomPlacesWithImages[0].loadedImages[0])}
                alt={randomPlacesWithImages[0].name}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs mt-1 text-gray-700 bg-white/70 px-2 rounded-md text-center">{randomPlacesWithImages[0].name}</p>
          </Link>
        )}

        {/* آیتم ۲ */}
        {randomPlacesWithImages[1] && (
          <Link
            to={`/places/${randomPlacesWithImages[1].place_id}`}
            className="place-link float3 absolute desktop:top-40 desktop:left-40 bottom-[22%] right-10 flex flex-col items-center w-24 hover:opacity-80 transition-opacity pointer-events-auto"
            style={{ zIndex: 70, cursor: 'pointer' }}
          >
            <div className="w-20 h-20 md:w-14 md:h-14 lg:w-24 lg:h-24 rounded-full overflow-hidden">
              <img
                src={getImageUrl(randomPlacesWithImages[1].loadedImages[0])}
                alt={randomPlacesWithImages[1].name}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs mt-1 text-gray-700 bg-white/70 px-2 rounded-md text-center">{randomPlacesWithImages[1].name}</p>
          </Link>
        )}

        {/* آیتم ۳ */}
        {randomPlacesWithImages[2] && (
          <Link
            to={`/places/${randomPlacesWithImages[2].place_id}`}
            className="place-link float3 absolute desktop:top-6 top-[20%] tablet:top-[10%] desktop:left-40 tablet:left-[12%] left-[10%] flex flex-col items-center w-24 hover:opacity-80 transition-opacity pointer-events-auto"
            style={{ zIndex: 70, cursor: 'pointer' }}
          >
            <div className="w-20 h-20 md:w-14 md:h-14 lg:w-24 lg:h-24 rounded-full overflow-hidden">
              <img
                src={getImageUrl(randomPlacesWithImages[2].loadedImages[0])}
                alt={randomPlacesWithImages[2].name}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs mt-1 text-gray-700 bg-white/70 px-2 rounded-md text-center">{randomPlacesWithImages[2].name}</p>
          </Link>
        )}

        {/* آیتم ۴ */}
        {randomPlacesWithImages[3] && (
          <Link
            to={`/places/${randomPlacesWithImages[3].place_id}`}
            className="place-link float3 absolute desktop:top-40 desktop:left-20 bottom-[22%] left-10 flex flex-col items-center w-24 hover:opacity-80 transition-opacity pointer-events-auto"
            style={{ zIndex: 70, cursor: 'pointer' }}
          >
            <div className="w-20 h-20 md:w-14 md:h-14 lg:w-24 lg:h-24 rounded-full overflow-hidden">
              <img
                src={getImageUrl(randomPlacesWithImages[3].loadedImages[0])}
                alt={randomPlacesWithImages[3].name}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs mt-1 text-gray-700 bg-white/70 px-2 rounded-md text-center">{randomPlacesWithImages[3].name}</p>
          </Link>
        )}

        {/* آیتم ۵ */}
        {randomPlacesWithImages[4] && (
          <Link
            to={`/places/${randomPlacesWithImages[4].place_id}`}
            className="place-link float3 absolute desktop:top-[60%] tablet:top-[60%] bottom-[2%] desktop:right-[30%] tablet:right-[24%] right-[40%] flex flex-col items-center w-24 hover:opacity-80 transition-opacity pointer-events-auto"
            style={{ zIndex: 70, cursor: 'pointer' }}
          >
            <div className="w-20 h-20 md:w-14 md:h-14 lg:w-24 lg:h-24 rounded-full overflow-hidden transform rotate-15">
              <img
                src={getImageUrl(randomPlacesWithImages[4].loadedImages[0])}
                alt={randomPlacesWithImages[4].name}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs mt-1 text-gray-700 bg-white/70 px-2 rounded-md text-center">{randomPlacesWithImages[4].name}</p>
          </Link>
        )}

        {/* آیتم ۶ */}
        {randomPlacesWithImages[5] && (
          <Link
            to={`/places/${randomPlacesWithImages[5].place_id}`}
            className="place-link float3 absolute desktop:top-[60%] tablet:top-[60%] top-[2%] desktop:left-[30%] tablet:left-[24%] left-[40%] flex flex-col items-center w-24 hover:opacity-80 transition-opacity pointer-events-auto"
            style={{ zIndex: 70, cursor: 'pointer' }}
          >
            <div className="w-20 h-20 md:w-14 md:h-14 lg:w-24 lg:h-24 rounded-full overflow-hidden transform rotate-15">
              <img
                src={getImageUrl(randomPlacesWithImages[5].loadedImages[0])}
                alt={randomPlacesWithImages[5].name}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs mt-1 text-gray-700 bg-white/70 px-2 rounded-md text-center">{randomPlacesWithImages[5].name}</p>
          </Link>
        )}
      </div>
    </div>
  );
};

export default ExperiencesSection;